<script setup lang="ts">
import type { TemplateCardView, TemplateSpecs } from '~/utils/template-catalog'

/**
 * The image chosen, and what choosing it means — at the top of the step, where
 * it stays visible while the customer keeps browsing below.
 *
 * It replaces a green banner that said only the name. What a buyer wants to
 * know once they have picked is how they will get in (a web panel and SSH for
 * an app, root over SSH or administrator over RDP for a system), that the
 * credentials arrive by e-mail, and whether the plan runs it: now that the image
 * is chosen BEFORE the plan, "needs a plan with at least 4 GB" is advice for the
 * next step rather than a refusal after it. A shortfall on a chosen plan is an
 * alert with its remedy, for apps and systems alike. A system's release is
 * chosen inside its tile; what that release means — its support window, its
 * end of life — is said here, where the customer reads the choice back.
 */
const props = defineProps<{
  view: TemplateCardView
  kind: 'app' | 'os'
  specs: TemplateSpecs
  remedyLabel?: string
  remedyHint?: string
}>()

const emit = defineEmits<{ remedy: [] }>()

const { t, locale } = useI18n()

const template = computed(() => props.view.template)

const access = computed(() => {
  if (props.kind === 'app') {
    return { icon: 'i-lucide-mail', text: t('billing.page.checkout.os.catalog.selected.accessApp') }
  }
  return template.value.os_family === 'windows'
    ? { icon: 'i-lucide-monitor', text: t('billing.page.checkout.os.catalog.selected.accessWindows') }
    : { icon: 'i-lucide-terminal', text: t('billing.page.checkout.os.catalog.selected.accessSsh') }
})

/** The minimum as the plan step will need it: disk only where it cannot grow. */
const minimum = computed(() =>
  specLabel({
    vcpu: template.value.min_vcpu,
    ramMb: template.value.min_ram_mb,
    diskGb: props.specs.diskAdjustable ? null : template.value.min_disk_gb,
  })
)

const planKnown = computed(() => props.specs.ramMb != null || props.specs.vcpu != null)

/**
 * A system release's support window, as icon + words. Silent when the platform
 * has said nothing (`support_status` null): a neutral "supported" line would be
 * a reassurance nobody checked.
 */
const support = computed(() => {
  const status = template.value.support_status
  if (props.kind !== 'os' || !status) return null
  const date = eolMonthLabel(template.value.eol_at, locale.value)
  return {
    ...SUPPORT_TONE[status],
    text:
      status === 'ended'
        ? t('billing.page.checkout.os.support.endedDetail', { date })
        : t('billing.page.checkout.os.support.supportedUntil', { date }),
  }
})

const requirement = computed(() => {
  if (!minimum.value || props.view.shortfall) return null
  return planKnown.value
    ? { icon: 'i-lucide-circle-check', tone: 'text-success-700 dark:text-success-400', text: t('billing.page.checkout.os.catalog.selected.fits') }
    : {
        icon: 'i-lucide-memory-stick',
        tone: 'text-muted',
        text: t('billing.page.checkout.os.catalog.selected.needsPlan', { spec: minimum.value }),
      }
})
</script>

<template>
  <div
    data-testid="template-selected"
    :data-template-code="template.code"
    class="on-tint rounded-xl border border-primary/30 bg-linear-to-br from-primary/10 via-primary/4 to-transparent p-3.5 dark:from-primary/15 dark:via-primary/5 @lg:p-4"
    aria-live="polite"
  >
    <div class="flex items-start gap-3">
      <TemplateLogo
        :icon-url="template.icon_url"
        :fallback="view.fallbackIcon"
        :accent="view.accent"
        size="lg"
      />
      <div class="min-w-0 flex-1">
        <p class="flex items-center gap-1.5 text-xs font-medium text-primary">
          <UIcon name="i-lucide-circle-check-big" class="size-3.5" />
          {{ $t('billing.page.checkout.os.selected') }}
        </p>
        <p class="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span class="text-base font-semibold leading-6 text-highlighted">{{ template.name }}</span>
          <span v-if="kind === 'app' && view.version" class="text-xs text-muted">{{ view.version }}</span>
          <UBadge v-if="view.isNew" data-testid="template-new-badge" color="primary" variant="soft" size="sm" class="self-center">
            {{ $t('billing.page.checkout.os.catalog.card.new') }}
          </UBadge>
          <span v-if="view.groupLabel" class="text-xs text-muted">{{ view.groupLabel }}</span>
        </p>
        <p v-if="view.tagline" class="mt-1 text-sm leading-5 text-toned">{{ view.tagline }}</p>
        <ul class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs leading-4 text-muted">
          <li class="inline-flex items-center gap-1.5">
            <UIcon :name="access.icon" class="size-3.5 shrink-0" />
            {{ access.text }}
          </li>
          <li v-if="requirement" :class="['inline-flex items-center gap-1.5', requirement.tone]">
            <UIcon :name="requirement.icon" class="size-3.5 shrink-0" />
            {{ requirement.text }}
          </li>
          <li v-if="support" :class="['inline-flex items-center gap-1.5', support.class]">
            <UIcon :name="support.icon" class="size-3.5 shrink-0" />
            {{ support.text }}
          </li>
        </ul>
      </div>
    </div>

    <!--
      End-of-life is a warning, never a refusal. Customers do run legacy
      workloads that need a dead release, and blocking the sale would only send
      them to a provider that does not tell them.
    -->
    <UAlert
      v-if="kind === 'os' && template.support_status === 'ended'"
      class="mt-3"
      color="error"
      variant="soft"
      icon="i-lucide-shield-off"
      :title="$t('billing.page.checkout.os.eolAlert.title', { name: template.name })"
      :description="$t('billing.page.checkout.os.eolAlert.description')"
    />

    <UAlert
      v-if="view.shortfall"
      class="mt-3"
      data-testid="template-requirement"
      color="warning"
      variant="soft"
      icon="i-lucide-alert-triangle"
      :title="$t('billing.page.checkout.os.requirementAlert.title', {
        name: template.name,
        spec: specLabel(view.shortfall),
      })"
      :description="remedyHint"
    >
      <template v-if="remedyLabel" #actions>
        <UButton color="warning" size="xs" @click="emit('remedy')">
          {{ remedyLabel }}
        </UButton>
      </template>
    </UAlert>
  </div>
</template>
